import { computed, makeObservable, reaction } from 'mobx';

import { FieldModel } from 'shared/models/form';
import { LoadingStageModel } from 'shared/models/loadingStage';
import {
  CabinetRequisitesServer,
  DaDataCompanyFieldsServer,
  DaDataCompany,
  LegalForm,
  LegalFormDataServer,
  OtherLegalForm,
  OtherLegalFormParams,
  validateBankAccount,
  validateBic,
  validateInn,
  validateKpp,
  validateOgrn,
  validatePhone,
  RequisitesError,
  mapRequisitesErrorToMessage
} from 'shared/entities/cabinet';
import { IRootStore } from 'shared/entities/store/rootStore';
import { apiUrls } from 'shared/entities/domain';
import { AppNotificationType } from 'shared/entities/appNotifications';
import { validateIsEmpty } from 'shared/entities/validator';
import { OpenStateModel } from 'shared/models/openState';
import PubSubObserver from 'lib/PubSubObserver';
import { parseCompanyKey } from 'shared/utils/companyKey';

const MAX_COMPANY_LIMIT = 20;
const validateOptionalPhone = (value: string) => {
  if (!value) {
    return null;
  }

  return validatePhone(value);
};

export default class CabinetRequisitesModel extends PubSubObserver {
  readonly cabinetId: string;
  readonly name: FieldModel<string>;
  readonly legalForm: FieldModel<LegalForm | OtherLegalForm.other | null>;
  readonly legalFormFull: FieldModel<string>;
  readonly legalFormShort: FieldModel<string>;
  readonly lpr: FieldModel<string>;
  readonly ogrn: FieldModel<string>;
  readonly inn: FieldModel<string>;
  readonly kpp: FieldModel<string>;
  readonly legalAddress: FieldModel<string>;
  readonly postAddress: FieldModel<string>;
  readonly phone: FieldModel<string>;
  //bank
  readonly bankAccount: FieldModel<string>;
  readonly bankBic: FieldModel<string>;
  readonly bankName: FieldModel<string>;
  readonly bankAddress: FieldModel<string>;
  readonly bankCorrAccount: FieldModel<string>;

  readonly company: FieldModel<string | null>;
  readonly getCompanyDataStage: LoadingStageModel = new LoadingStageModel();
  readonly companySelectorOpened: OpenStateModel = new OpenStateModel();

  readonly savingStage: LoadingStageModel = new LoadingStageModel();
  readonly saved: FieldModel<boolean>;
  readonly rootStore: IRootStore;

  constructor(
    {
      name,
      legalForm,
      otherLegalFormParams,
      kpp,
      legalAddress,
      phone,
      postAddress,
      lpr,
      ogrn,
      inn,
      bankAccount,
      bankBic,
      bankAddress,
      bankCorrAccount,
      bankName,
      cabinetId,
      saved
    }: {
      name: string;
      legalForm: LegalForm | OtherLegalForm.other | null;
      otherLegalFormParams: OtherLegalFormParams;
      lpr: string;
      ogrn: string;
      inn: string;
      kpp: string;
      legalAddress: string;
      postAddress: string;
      phone: string;
      bankAccount: string;
      bankBic: string;
      bankName: string;
      bankAddress: string;
      bankCorrAccount: string;
      cabinetId: string;
      saved: boolean;
    },
    rootStore: IRootStore
  ) {
    super();
    this.cabinetId = cabinetId;
    this.name = new FieldModel<string>(name, [validateIsEmpty]);
    this.legalForm = new FieldModel<LegalForm | OtherLegalForm.other | null>(
      legalForm,
      [validateIsEmpty]
    );
    this.legalFormFull = new FieldModel(otherLegalFormParams.legalFormFull, [
      validateIsEmpty
    ]);
    this.legalFormShort = new FieldModel(otherLegalFormParams.legalFormShort, [
      validateIsEmpty
    ]);

    this.kpp = new FieldModel<string>(kpp);
    this.lpr = new FieldModel<string>(lpr);
    this.ogrn = new FieldModel<string>(ogrn, [validateOgrn]);
    this.inn = new FieldModel<string>(inn, [validateInn]);
    this.legalAddress = new FieldModel<string>(legalAddress, [validateIsEmpty]);
    this.postAddress = new FieldModel<string>(postAddress);
    this.phone = new FieldModel<string>(phone, [validateOptionalPhone]);
    this.bankAccount = new FieldModel<string>(bankAccount, [
      validateBankAccount
    ]);
    this.bankBic = new FieldModel<string>(bankBic, [validateBic]);
    this.bankName = new FieldModel<string>(bankName, [validateIsEmpty]);
    this.bankAddress = new FieldModel<string>(bankAddress);
    this.bankCorrAccount = new FieldModel<string>(bankCorrAccount);
    this.saved = new FieldModel<boolean>(saved);

    this.company = new FieldModel<string | null>(null);

    this.rootStore = rootStore;

    makeObservable(this, {
      isError: computed,
      withOtherLegalForm: computed,
      touched: computed
    });
  }

  get isError(): boolean {
    return (
      this.name.isError ||
      this.legalForm.isError ||
      this.kpp.isError ||
      this.ogrn.isError ||
      this.inn.isError ||
      this.bankBic.isError ||
      this.bankName.isError ||
      this.phone.isError ||
      this.legalAddress.isError ||
      this.bankAccount.isError ||
      (this.legalForm.value === OtherLegalForm.other &&
        (this.legalFormFull.isError || this.legalFormShort.isError))
    );
  }

  get touched(): boolean {
    return (
      this.legalForm.touched ||
      this.kpp.touched ||
      this.ogrn.touched ||
      this.inn.touched ||
      (this.legalForm.value === OtherLegalForm.other &&
        (this.legalFormFull.touched || this.legalFormShort.touched)) ||
      this.name.touched ||
      this.lpr.touched ||
      this.legalAddress.touched ||
      this.postAddress.touched ||
      this.phone.touched ||
      this.bankAccount.touched ||
      this.bankBic.touched ||
      this.bankName.touched ||
      this.bankAddress.touched ||
      this.bankCorrAccount.touched
    );
  }

  get withOtherLegalForm(): boolean {
    return this.legalForm.value === OtherLegalForm.other;
  }

  get companySearchTitle(): string {
    return this.name.value && this.inn.value
      ? `${this.name.value} ${this.inn.value}`
      : '';
  }

  changeLegalForm = (value: LegalForm | OtherLegalForm | null) => {
    this.legalForm.changeValue(value);
    if (value === LegalForm.ip) {
      this.validateKpp();
    }
  };

  validateKpp = () => {
    const error = validateKpp(
      this.kpp.value,
      this.legalForm.value !== LegalForm.ip
    );
    this.kpp.setError(error);
    return error;
  };

  validate = (): void => {
    this.name.validate();
    this.legalForm.validate();
    this.validateKpp();
    this.ogrn.validate();
    this.inn.validate();
    this.bankBic.validate();
    this.bankName.validate();
    this.phone.validate();
    this.legalAddress.validate();
    this.bankAccount.validate();

    if (this.withOtherLegalForm) {
      this.legalFormFull.validate();
      this.legalFormShort.validate();
    }
  };

  save = async (withNotification = true): Promise<BaseResponse> => {
    this.validate();

    if (!this.touched && !this.isError) {
      return {
        isError: false
      };
    }

    const json = this.toJson();
    if (this.savingStage.isLoading || !json || this.isError) {
      return {
        isError: true
      };
    }

    this.savingStage.loading();

    const response = await this.rootStore.networkStore.api<{}, RequisitesError>(
      apiUrls.PAYMENT_DOCUMENTS_SET_REQUISITES,
      {
        method: 'POST',
        data: json,
        errorsMap: mapRequisitesErrorToMessage
      }
    );

    if (response.isError) {
      this.savingStage.error();
    } else {
      this.savingStage.success();
      if (withNotification) {
        this.rootStore.appNotificationsStore.open({
          title: (t) =>
            t('cabinet.CabinetRequisiteModel.notification', { ns: 'models' }),
          type: AppNotificationType.success
        });
      }
      this.resetTouched();
      this.saved.changeValue(true);
    }

    return {
      isError: response.isError
    };
  };

  toJson(): CabinetRequisitesServer | null {
    if (!this.legalForm.value) {
      return null;
    }

    let legalFormData: LegalFormDataServer;
    if (this.legalForm.value === OtherLegalForm.other) {
      legalFormData = {
        legal_form: this.legalFormShort.value,
        legal_form_full: this.legalFormFull.value
      };
    } else {
      legalFormData = {
        legal_form: this.legalForm.value
      };
    }

    return {
      name: this.name.value,
      lpr: this.lpr.value,
      ogrn: this.ogrn.value,
      inn: this.inn.value,
      kpp: this.kpp.value,
      legal_address: {
        value: this.legalAddress.value
      },
      post_address: this.postAddress.value,
      phone: this.phone.value,
      bank: {
        account: this.bankAccount.value,
        bic: this.bankBic.value,
        name: this.bankName.value,
        address: this.bankAddress.value,
        corr_account: this.bankCorrAccount.value
      },
      ...legalFormData
    };
  }

  resetTouched = () => {
    this.legalForm.resetTouched();
    this.kpp.resetTouched();
    this.ogrn.resetTouched();
    this.inn.resetTouched();
    this.name.resetTouched();
    this.lpr.resetTouched();
    this.legalAddress.resetTouched();
    this.postAddress.resetTouched();
    this.phone.resetTouched();
    this.bankAccount.resetTouched();
    this.bankBic.resetTouched();
    this.bankName.resetTouched();
    this.bankAddress.resetTouched();
    this.bankCorrAccount.resetTouched();

    if (this.legalForm.value === OtherLegalForm.other) {
      this.legalFormFull.resetTouched();
      this.legalFormShort.resetTouched();
    }
  };

  setDaDataRequisites = (data: DaDataCompanyFieldsServer) => {
    this.name.changeValue(data.name);
    this.inn.changeValue(data.inn);
    this.ogrn.changeValue(data.ogrn);
    this.legalAddress.changeValue(data.legal_address.value);

    if (data.kpp) {
      this.kpp.changeValue(data.kpp);
    }

    if (data.director_name) {
      this.lpr.changeValue(data.director_name);
    }

    if (Object.values(LegalForm).includes(data.legal_form as LegalForm)) {
      this.changeLegalForm(data.legal_form as LegalForm);
    } else {
      this.changeLegalForm(OtherLegalForm.other);
      this.legalFormShort.changeValue(data.legal_form);
    }
  };

  loadCompanyList = async (
    query: string,
    abortController: AbortController
  ): Promise<BaseResponse<DaDataCompany[]>> => {
    const response = await this.rootStore.networkStore.api<{
      companies: DaDataCompany[];
    }>(apiUrls.DADATA_COMPANIES_LIST, {
      method: 'GET',
      data: {
        search: query,
        limit: MAX_COMPANY_LIMIT
      },
      showUnexpectedError: false,
      config: {
        signal: abortController.signal
      }
    });

    if (response.isError) {
      return {
        isError: true
      };
    }

    return { isError: false, data: response.data.companies };
  };

  getCompanyData = async () => {
    if (this.getCompanyDataStage.isLoading || !this.company.value) {
      return { isError: true };
    }

    this.getCompanyDataStage.loading();
    const inn = parseCompanyKey(this.company.value);
    const response = await this.rootStore.networkStore.api<{
      company: DaDataCompanyFieldsServer;
    }>(apiUrls.DADATA_COMPANIES_GET, {
      method: 'GET',
      data: {
        inn
      }
    });

    if (response.isError) {
      this.getCompanyDataStage.error();

      return {
        isError: true
      };
    }

    this.setDaDataRequisites(response.data.company);
    this.getCompanyDataStage.success();
    return { isError: false };
  };

  subscribe(): void {
    this.addReaction({
      key: 'company',
      reaction: reaction(() => this.company.value, this.getCompanyData)
    });
  }

  reset = () => {
    this.legalForm.reset();
    this.kpp.reset();
    this.ogrn.reset();
    this.inn.reset();
    this.name.reset();
    this.lpr.reset();
    this.legalAddress.reset();
    this.postAddress.reset();
    this.phone.reset();
    this.bankAccount.reset();
    this.bankBic.reset();
    this.bankName.reset();
    this.bankAddress.reset();
    this.bankCorrAccount.reset();

    if (this.legalForm.value === OtherLegalForm.other) {
      this.legalFormFull.reset();
      this.legalFormShort.reset();
    }

    this.disposeAll();
  };

  static fromDefaultParams(
    cabinetId: string,
    rootStore: IRootStore
  ): CabinetRequisitesModel {
    return new CabinetRequisitesModel(
      {
        legalForm: null,
        name: '',
        kpp: '',
        legalAddress: '',
        postAddress: '',
        phone: '',
        lpr: '',
        ogrn: '',
        inn: '',
        bankName: '',
        bankCorrAccount: '',
        bankAddress: '',
        bankBic: '',
        bankAccount: '',
        cabinetId,
        saved: false,
        otherLegalFormParams: { legalFormFull: '', legalFormShort: '' }
      },
      rootStore
    );
  }

  static fromJson(
    {
      name,
      kpp,
      legal_address,
      phone,
      post_address,
      inn,
      lpr,
      ogrn,
      bank,
      ...legalFormData
    }: CabinetRequisitesServer,
    cabinetId: string,
    rootStore: IRootStore
  ): CabinetRequisitesModel {
    const params = legalFormData.legal_form_full
      ? {
          legalFormFull: legalFormData.legal_form_full,
          legalFormShort: legalFormData.legal_form
        }
      : { legalFormFull: '', legalFormShort: '' };

    return new CabinetRequisitesModel(
      {
        name,
        legalForm: legalFormData.legal_form_full
          ? OtherLegalForm.other
          : (legalFormData.legal_form as LegalForm),
        otherLegalFormParams: params,
        kpp,
        legalAddress: legal_address.value,
        phone,
        postAddress: post_address,
        lpr,
        ogrn,
        inn,
        bankName: bank.name,
        bankCorrAccount: bank.corr_account,
        bankAddress: bank.address,
        bankBic: bank.bic,
        bankAccount: bank.account,
        cabinetId,
        saved: true
      },
      rootStore
    );
  }
}
